import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { companyApis } from '@apis/dashboard/companyApis';
import { dashboardContactApis } from '@apis/next/contactApis';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { AutoCompleteConfig } from '@components/NextForms/TextInput';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { MobxZodField, getDecodeResult, getDecodeResultOr } from '@monoid-dev/mobx-zod-form';
import { runInAction } from 'mobx';
import { pipe, uniqBy, filter } from 'remeda';
import type { ZodNumber, ZodString } from 'zod';

export interface UseClientIdAutoCompleteOptions {
  client_id: MobxZodField<ZodNumber>;
  client_code: MobxZodField<ZodString>;
  client_name: MobxZodField<ZodString>;
  client_name_en: MobxZodField<ZodString>;
  client_zipcode: MobxZodField<ZodString>;
  client_address1: MobxZodField<ZodString>;
  client_address2: MobxZodField<ZodString>;
}

export type ClientIdAutoComplete = AutoCompleteConfig<{
  client_id: number;
  client_name: string;
}>;

export const useClientIdAutoComplete = (
  options: UseClientIdAutoCompleteOptions,
): ClientIdAutoComplete => {
  const {
    client_id,
    client_code,
    client_name,
    client_name_en,
    client_zipcode,
    client_address1,
    client_address2,
  } = options;

  const selectedClientName = useRef(getDecodeResultOr(client_name.decodeResult, ''));

  // If the user changes client_name,
  // we need to reset the client_id,
  // because the actual client_id needs to come from the auto complete.
  useEffect(() => {
    if (
      client_name.touched &&
      getDecodeResultOr(client_name.decodeResult, '') !== selectedClientName.current
    ) {
      client_id.setOutput(0);
    }
  }, [client_name.touched, getDecodeResultOr(client_name.decodeResult, '')]);

  const clientName = pipe(getDecodeResultOr(client_name.decodeResult, ''), (v) =>
    useDebouncedValue(() => v.trim(), 100, [v]),
  );

  const { bookingInquiry } = useContext(BookingDetailsContext);

  const contactsQuery = dashboardContactApis.useGet(
    '/dashboard/contact',
    {
      queries: {
        company_name: clientName,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const relatedIds = [
    bookingInquiry.consignee?.user,
    bookingInquiry.owner_company?.id,
    bookingInquiry.shipper?.user,
  ];

  const autoCompleteOptions: ClientIdAutoComplete['autoCompleteOptions'] = useMemo(() => {
    const results = pipe(
      [
        {
          label: `${bookingInquiry.owner_company?.company_name} (OWNER)`,
          value: {
            client_id: relatedIds[1],
            client_name: bookingInquiry.owner_company?.company_name ?? '',
          },
        },
        {
          label: `${bookingInquiry.consignee?.company_name} (CNEE)`,
          value: {
            client_id: relatedIds[0],
            client_name: bookingInquiry.consignee?.company_name ?? '',
          },
        },
        {
          label: `${bookingInquiry.shipper?.company_name} (SHIPPER)`,
          value: {
            client_id: relatedIds[2],
            client_name: bookingInquiry.shipper?.company_name ?? '',
          },
        },
        ...(contactsQuery.data ?? []).map((contact) => ({
          label: contact.company_name,
          value: {
            client_id: contact.user,
            client_name: contact.company_name,
          },
        })),
      ],
      filter(
        (v): v is ClientIdAutoComplete['autoCompleteOptions'][number] =>
          v.value.client_id != null && v.value.client_name.trim() !== '',
      ),
      uniqBy((v) => v.value.client_id),
    );

    const lastRelatedIndex = results.findLastIndex((o) => relatedIds.includes(o.value.client_id));

    if (lastRelatedIndex !== -1) {
      results[lastRelatedIndex].splitter = true;
    }

    return results;
  }, [contactsQuery.data]);

  const fillFieldsForClientId = async (id: number) => {
    const contact = await companyApis.get('/dashboard/company/{id}/detail/', {
      path: { id },
    });

    runInAction(() => {
      if (contact[200]) {
        client_code.setOutput(contact[200].member_no);
        client_zipcode.setOutput(contact[200].zip_code);
        client_name_en.setOutput(contact[200].english_name);
        client_address1.setOutput(contact[200].address1);
        client_address2.setOutput(contact[200].address2);
      } else {
        throw new Error('Cannot get company details. ');
      }
    });
  };

  useEffect(() => {
    if (bookingInquiry.owner_company && getDecodeResult(client_id.decodeResult) === 0) {
      client_id.setOutput(bookingInquiry.owner_company.id);
      selectedClientName.current = bookingInquiry.owner_company.company_name;
      client_name.setOutput(bookingInquiry.owner_company.company_name);
      fillFieldsForClientId(bookingInquiry.owner_company.id);
    }
  }, []);

  return {
    autoCompleteOptions,
    filter(value, options) {
      return options.filter(
        (option) =>
          option.label.toUpperCase().includes(String(value).toUpperCase()) ||
          option.value.client_name.toUpperCase().includes(String(value).toUpperCase()),
      );
    },
    onSelect: useCallback(
      async (option: ClientIdAutoComplete['autoCompleteOptions'][number]) => {
        runInAction(() => {
          client_id.setOutput(option.value.client_id);
          selectedClientName.current = option.value.client_name;
          client_name.setOutput(option.value.client_name);
        });

        await fillFieldsForClientId(option.value.client_id);
      },
      [client_id, client_name],
    ),
  };
};
